/**
 * Shared integrity-signal rendering helpers for the admin_v2 dashboards.
 *
 * The same chip strip renders across three surfaces:
 *   - Live participants table (per-player row)
 *   - Duel list + duel detail (per-duel + per-run)
 *   - Retest detail (per-submission)
 *
 * The aggregates shape is the same on every backend endpoint:
 *
 *   { any_untrusted: bool, decoy_hit: bool, overlay_detected: bool,
 *     visibility_changes: int }
 *
 * The chips read colour based on severity:
 *   - decoy_hit / overlay_detected / any_untrusted → red (strong bot tells)
 *   - visibility_changes > 0 → amber (behavioural, weaker tell)
 *   - nothing observed → soft green "clean" chip so the reviewer can see
 *     at a glance that the submission is unremarkable.
 *
 * Deliberately not styled with adm2 CSS variables — palette is hardcoded
 * so the chips stay consistent between dark + light themes without
 * having to touch tokens.css.
 */

import { escape } from '../../components/form.js';

const CHIP_BASE =
  'padding:3px 8px;border-radius:999px;font:700 10px \'IBM Plex Sans\',sans-serif;' +
  'letter-spacing:.05em;text-transform:uppercase;white-space:nowrap;';

const CHIP_GREEN =
  CHIP_BASE +
  'background:rgba(74,222,128,.12);color:#4ADE80;border:1px solid rgba(74,222,128,.35);';
const CHIP_AMBER =
  CHIP_BASE +
  'background:rgba(251,191,36,.14);color:#FBBF24;border:1px solid rgba(251,191,36,.4);';
const CHIP_RED =
  CHIP_BASE +
  'background:rgba(248,113,113,.14);color:#F87171;border:1px solid rgba(248,113,113,.4);';
const CHIP_MUTED =
  CHIP_BASE +
  'background:rgba(148,163,184,.12);color:var(--a-muted,#94A3B8);border:1px solid rgba(148,163,184,.25);';

/**
 * Returns a normalised aggregates object even when the caller passes
 * `null` / partial data (any of the three endpoints may return
 * `null` for players who haven't answered yet).
 */
export function normaliseAggregates(agg) {
  agg = agg || {};
  return {
    any_untrusted: !!agg.any_untrusted,
    decoy_hit: !!agg.decoy_hit,
    overlay_detected: !!agg.overlay_detected,
    visibility_changes: Number(agg.visibility_changes || 0),
  };
}

/**
 * Renders the chip strip. Compact mode renders just the offending
 * chips (or one "clean" chip) — used inside table rows where space is
 * tight. Full mode renders every dimension so the detail modals /
 * pages show the same-shaped strip everywhere.
 */
export function integrityChips(agg, { compact = false } = {}) {
  const a = normaliseAggregates(agg);
  const anyBad = a.any_untrusted || a.decoy_hit || a.overlay_detected;
  if (compact) {
    if (!anyBad && !a.visibility_changes) {
      return `<span style="${CHIP_GREEN}">clean</span>`;
    }
    const chips = [];
    if (a.decoy_hit) chips.push(`<span style="${CHIP_RED}">decoy hit</span>`);
    if (a.any_untrusted) chips.push(`<span style="${CHIP_RED}">untrusted</span>`);
    if (a.overlay_detected) chips.push(`<span style="${CHIP_RED}">overlay</span>`);
    if (a.visibility_changes > 0) {
      chips.push(
        `<span style="${CHIP_AMBER}">vis ${escape(String(a.visibility_changes))}</span>`,
      );
    }
    return `<div style="display:flex;flex-wrap:wrap;gap:4px;">${chips.join('')}</div>`;
  }
  // Full mode — always renders every axis, using the muted chip for
  // "not observed" so the strip reads the same width every time.
  return `
    <div style="display:flex;flex-wrap:wrap;gap:6px;">
      <span style="${a.any_untrusted ? CHIP_RED : CHIP_GREEN}">${a.any_untrusted ? 'untrusted click' : 'trusted clicks'}</span>
      <span style="${a.decoy_hit ? CHIP_RED : CHIP_MUTED}">${a.decoy_hit ? 'decoy hit' : 'no decoy'}</span>
      <span style="${a.overlay_detected ? CHIP_RED : CHIP_MUTED}">${a.overlay_detected ? 'overlay observed' : 'no overlay'}</span>
      <span style="${a.visibility_changes > 0 ? CHIP_AMBER : CHIP_MUTED}">vis-changes ${escape(String(a.visibility_changes))}</span>
    </div>
  `;
}

/**
 * Sort key for a signals row. Returns a positive integer that ranks
 * the worst signals highest so `sortDesc(integritySortKey)` puts the
 * bot-tells at the top of the list. Used by table columns.
 */
export function integritySortKey(agg) {
  const a = normaliseAggregates(agg);
  let n = 0;
  if (a.decoy_hit) n += 1000;
  if (a.any_untrusted) n += 500;
  if (a.overlay_detected) n += 250;
  n += Math.min(100, a.visibility_changes);
  return n;
}

/**
 * Renders one per-answer signals sub-row for a detail page — shows the
 * raw `is_trusted`, `pointer_type`, `ms_since_render`, and any
 * anomaly flags. Rendered inline under an answer's prompt.
 */
export function perAnswerSignalsHtml(signals) {
  if (!signals) {
    return '<span style="font-size:11px;color:var(--a-faint,#8595B2);">no signals reported</span>';
  }
  const bits = [];
  if (signals.is_trusted === false) {
    bits.push(`<span style="${CHIP_RED}">untrusted</span>`);
  } else if (signals.is_trusted === true) {
    bits.push(`<span style="${CHIP_GREEN}">trusted</span>`);
  }
  if (signals.decoy_hit) bits.push(`<span style="${CHIP_RED}">decoy</span>`);
  if (signals.overlay_detected) bits.push(`<span style="${CHIP_RED}">overlay</span>`);
  if (typeof signals.visibility_changes === 'number' && signals.visibility_changes > 0) {
    bits.push(
      `<span style="${CHIP_AMBER}">vis ${escape(String(signals.visibility_changes))}</span>`,
    );
  }
  if (signals.pointer_type) {
    bits.push(
      `<span style="${CHIP_MUTED}">${escape(String(signals.pointer_type))}</span>`,
    );
  }
  if (typeof signals.ms_since_render === 'number') {
    bits.push(
      `<span style="${CHIP_MUTED}">${escape(String(signals.ms_since_render))}ms client</span>`,
    );
  }
  if (!bits.length) return '<span style="font-size:11px;color:var(--a-faint,#8595B2);">no anomalies</span>';
  return `<div style="display:flex;flex-wrap:wrap;gap:4px;">${bits.join('')}</div>`;
}
